import React from 'react';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { get, values, findIndex, map, groupBy, reduce } from 'lodash';
import { formatToPercent } from '../../utils/shared';


export const engagementTotalDefaultSorts = ['episodeString'];
export const engagementTotalColumns = [
  {
    Header: 'Season | Episode',
    accessor: 'episodeString',
    className: 'tvt-tabular-data-table__show-report-engagement-totals-episode',
    sortType: (rowA, rowB) =>
      rowB.original.episodeSort - rowA.original.episodeSort
  },
  {
    Header: 'Viewers',
    accessor: 'viewers',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-viewers',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true
  },
  {
    Header: 'Engagers',
    accessor: 'engagers',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-engagers',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true
  },
  {
    Header: 'Reactions',
    accessor: 'ratings',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-reactions',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true
  },
  {
    Header: 'Character Votes',
    accessor: 'characterVotes',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-characterVotes caret-center',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true
  },
  {
    Header: 'Total Engagements',
    accessor: 'engagements',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-engagements caret-center',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true
  },
  {
    Header: 'Engagement Score',
    accessor: 'engagementScore',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-engagement-totals-engagementScore caret-center',
    Cell: ({ cell: { value } }) => numeral(value).format('0.00'),
    sortDescFirst: true
  }
];

export const anticipationAffinityDefaultSorts = ['affinityIndex'];

export const anticipationAffinityColumns = [
  {
    Header: 'Show',
    accessor: 'show',
    className: 'tvt-tabular-data-table__show-report-anticipation-show-name',
    Cell: ({ row: { original }, cell: { value } }) => (
      <Link to={`/show/${original.showId}`}>{value}</Link>
    )
  },
  {
    Header: 'Network',
    accessor: 'network',
    Cell: ({ row: { original }, cell: { value } }) => (
      <Link to={`/network/${original.networkId}`}>{value}</Link>
    ),
    className: 'tvt-tabular-data-table__show-report-anticipation-network'
  },
  {
    Header: 'Overlapping Viewers',
    accessor: 'overlappingViewers',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true,
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-overlapping-viewers caret-center'
  },
  {
    Header: 'Affinity Index',
    accessor: 'affinityIndex',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-affinity-index  caret-center',
    sortDescFirst: true
  }
];

export const audienceAffinityDefaultSorts = ['affinityIndex'];
export const audienceAffinityColumns = anticipationAffinityColumns;

export const audienceWAVWColumnsDefaultSorts = [];
export const audienceWAVWColumns = [
  {
    Header: 'Platform',
    accessor: 'platformName',
    className: 'tvt-tabular-data-table__show-report-anticipation-wavw-platform'
  },
  {
    Header: 'Views',
    accessor: 'voteCount',
    sortDescFirst: true,
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortType: 'basic',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-wavw-views'
  },
  {
    Header: '% Views',
    accessor: 'voteShare',
    Cell: ({ cell: { value } }) => formatToPercent(value),
    sortType: 'basic',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-wavw-views-percent',
    sortDescFirst: true
  }
]

export const showAnticipationFollowersTotal = data =>
  data.map(obj => ({
    x: obj.date,
    yLabel: 'Followers',
    yLabelPercent: 'Growth Rate',
    percentChange: get(obj, 'newFollowers', 0) / get(obj, 'totalFollowers', 1),
    y: obj.totalFollowers
  }));

export const showAnticipationFollowersNew = data =>
  data.map(obj => ({
    x: obj.date,
    yLabel: 'New Followers',
    percentChange: get(obj, 'newFollowers', 0) / get(obj, 'totalFollowers', 1),
    y: obj.newFollowers
  }));

export const formatGenderDemo = ({ data, loading }) => {
  if (loading) {
    return [];
  }

  return data.map(g => {
    const { gender, index, viewers } = g;
    const viewerIndex = numeral(index * 100).format('0.0');
    return {
      id: gender,
      viewers,
      label: (
        <tspan>
          <tspan className="user-demo-tspan__gender-gender">{gender}</tspan>
          <tspan className="user-demo-tspan__gender-viewerIndex" x={80}>
            {`${viewerIndex}%`}
          </tspan>
        </tspan>
      ),
      value: viewerIndex
    };
  });
};

export const formatCharacterVoteByEpisode = data => {
  const obj = {};
  const arr = [];
  // arr.push({ x: '', y: null, episodeSort: 0 });

  data.forEach(d => {
    if (!arr.find(o => o.x === d.x)) {
      arr.push({ x: d.x, y: null, episodeSort: d.episodeSort });
    }
  });

  data.forEach(d => {
    if (obj[d.actorId]) {
      const replaceIndex = findIndex(arr, o => o.episodeSort === d.episodeSort);
      obj[d.actorId].data[replaceIndex] = d;
      obj[d.actorId].voteCount += d.votes;
    } else {
      const actorData = [...arr];
      const replaceIndex = findIndex(arr, o => o.episodeSort === d.episodeSort);
      actorData[replaceIndex] = d;
      const { actorName, characterImageUrl, characterName, votes } = d;
      obj[d.actorId] = {
        id: d.actorId,
        actorName,
        characterImageUrl,
        characterName,
        data: actorData,
        voteCount: votes
      };
    }

    if (!arr.find(o => o.x === d.x)) {
      arr.push({ x: d.x, y: null });
    }
  });

  return values(obj).sort((a, b) => b.voteCount - a.voteCount);
};

export const getUniqueEpisodeLabels = data => {
  const arr = [];

  return arr;
};

export const TabbedFilterTooltip = (
  <>
    <div>
      Filter series data by user demographics and date, or apply a saved set of
      filters.{' '}
    </div>
    <div>These filters apply to the top Score Bar values and all modules.</div>
  </>
);

const colors = [
  '#491d8b',
  '#6929c4',
  '#8a3ffc',
  '#a56eff',
  '#be95ff',
  '#d4bbff',
  '#e8daff',
  '#f6f2ff',
  '#d9fbfb',
  '#9ef0f0',
  '#3ddbd9',
  '#08bdba',
  '#009d9a',
  '#007d79',
  '#005d5d',
  '#004144'
];

const getEmotionColor = (val, MAX_VALUE = 1) => {
  const indexer = Math.floor(colors.length / 2);
  const segmentSize = MAX_VALUE / indexer;

  const colorIndex = Math.floor(val / segmentSize + indexer);
  return colors[colorIndex < colors.length ? colorIndex : colors.length - 1];
};

export const emotionMapping = {
  shocked: String.fromCodePoint(0x1f635),
  sad: String.fromCodePoint(0x1f62d),
  amused: String.fromCodePoint(0x1f606),
  thrilled: String.fromCodePoint(0x1f929),
  confused: String.fromCodePoint(0x1f642),
  bored: String.fromCodePoint(0x1f611),
  scared: String.fromCodePoint(0x1f631),
  frustrated: String.fromCodePoint(0x1f624),
  touched: String.fromCodePoint(0x1f97a),
  reflective: '🤔',
  tense: '😬',
  understood: '😌'
};

export const formatEmotionVotesByEpisode = (data = []) =>
  map(
    groupBy(
      map(data, row => ({
        episodeString: row.episodeString,
        episodeSort: row.episodeSort,
        [row.emotion]: numeral(row.emotionPercent).format('0.00'),
        [`${row.emotion}Votes`]: row.emotionCount,
        [`${row.emotion}Color`]: getEmotionColor(row.emotionPercent)
      })),
      'episodeString'
    ),
    row => reduce(row, (acc, r) => ({ ...acc, ...r }), {})
  );
